/**
 * Integer arithmetic helpers for 6th-grade operations.
 * Validates integer operations with sign rules.
 */
import { Rational, rat } from "./rational";

export function validateAddition(a: Rational, b: Rational, expected: Rational): boolean {
  return a.add(b).equals(expected);
}

export function validateSubtraction(a: Rational, b: Rational, expected: Rational): boolean {
  return a.sub(b).equals(expected);
}

export function validateMultiplication(a: Rational, b: Rational, expected: Rational): boolean {
  return a.mul(b).equals(expected);
}

export function validateDivision(a: Rational, b: Rational, expected: Rational): boolean {
  if (b.isZero()) return false;
  return a.div(b).equals(expected);
}

export function absoluteValue(a: Rational): Rational {
  return a.abs();
}

export function opposite(a: Rational): Rational {
  return a.neg();
}

export function productSign(a: Rational, b: Rational): -1 | 0 | 1 {
  if (a.isZero() || b.isZero()) return 0;
  if ((a.isNegative() && b.isNegative()) || (a.isPositive() && b.isPositive())) return 1;
  return -1;
}

/** GCD for whole numbers. */
export function gcdInt(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  return a;
}

/** LCM for whole numbers. */
export function lcmInt(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return Math.abs(a * b) / gcdInt(a, b);
}

/** Power for non-negative integer exponents. */
export function intPower(base: Rational, exponent: number): Rational {
  if (exponent < 0) throw new RangeError("Negative exponents not supported");
  if (exponent === 0) return Rational.ONE;
  let result = Rational.ONE;
  for (let i = 0; i < exponent; i++) result = result.mul(base);
  return result;
}

/** Distance between two integers on a number line. */
export function numberLineDistance(a: Rational, b: Rational): Rational {
  return a.sub(b).abs();
}

/**
 * Parse a decimal string like "3.14" to a Rational. Returns null if invalid.
 * Used by generators that need to display answers as decimals.
 */
export function decimalToRational(s: string): Rational | null {
  const m = /^([+-]?\d*\.\d+)$/.exec(s.trim());
  if (!m) return null;
  const str = m[1] as string;
  const dotIdx = str.indexOf(".");
  const intPart = str.slice(0, dotIdx).replace(/^([+-]?)$/, "$10");
  const fracPart = str.slice(dotIdx + 1);
  const whole = intPart + fracPart;
  const den = 10 ** fracPart.length;
  return rat(BigInt(whole), BigInt(den));
}

/**
 * Format a Rational as a decimal string with the specified number of places.
 * Uses truncation to integer digits when the value is exact.
 */
export function rationalToDecimal(value: Rational, places: number): string {
  const num = value.toNumber();
  return num.toFixed(places);
}
